const STORAGE_KEY = "bc-diag-log";
const MAX_ENTRIES = 60;

export type ClientLogKind = "error" | "rejection" | "network" | "info";

export interface ClientLogEntry {
  t: string;
  kind: ClientLogKind;
  msg: string;
  detail?: string;
  url?: string;
  status?: number;
  code?: string;
  ms?: number;
}

export function clientContext() {
  let timezone = "unknown";
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* Intl unavailable */
  }
  return {
    timezone,
    offsetMinutes: new Date().getTimezoneOffset(),
    locale: navigator.language,
    languages: Array.isArray(navigator.languages)
      ? navigator.languages.join(",")
      : "",
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    href: window.location.href,
  };
}

export function getEvents(): ClientLogEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearEvents(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable */
  }
}

export function logEvent(entry: Omit<ClientLogEntry, "t">): void {
  const full: ClientLogEntry = { t: new Date().toISOString(), ...entry };
  if (import.meta.env.DEV) {
    console.warn("[diag]", full);
  }
  try {
    const events = getEvents();
    events.push(full);
    if (events.length > MAX_ENTRIES) {
      events.splice(0, events.length - MAX_ENTRIES);
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    /* storage full/unavailable — drop silently */
  }
}

let installed = false;

export function installGlobalErrorLogging(): void {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    logEvent({
      kind: "error",
      msg: event.message || "window.onerror",
      detail: event.error?.stack || `${event.filename}:${event.lineno}`,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    logEvent({
      kind: "rejection",
      msg:
        (reason && (reason.message || String(reason))) ||
        "unhandledrejection",
      detail: reason?.stack,
    });
  });
}
